/**
 * DedupeWarningBanner Component
 * 
 * Shows a warning when an operation being added already exists elsewhere in the estimate.
 * Used at apply-time to alert FOH before adding duplicate work.
 */
import React from 'react';
import { AlertTriangle, Info, XCircle, ChevronRight } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

export const DedupeWarningBanner = ({ 
  warning, 
  onDismiss,
  onViewExisting 
}) => {
  if (!warning || !warning.exists_elsewhere) return null;

  return (
    <Alert className="mb-3 border-amber-300 bg-amber-50">
      <AlertTriangle className="h-4 w-4 text-amber-600" />
      <AlertDescription className="text-amber-800">
        <div className="flex items-center justify-between">
          <div>
            <span className="font-medium">Duplicate detected: </span>
            This operation already exists in "{warning.existing_group_title}"
            {warning.existing_line_description && (
              <span className="text-amber-600 ml-1">({warning.existing_line_description})</span>
            )}
          </div>
          <div className="flex gap-2">
            {onViewExisting && (
              <button 
                onClick={onViewExisting}
                className="text-amber-700 hover:text-amber-900 text-sm underline flex items-center gap-1"
              >
                View <ChevronRight className="h-3 w-3" />
              </button>
            )}
            {onDismiss && (
              <button 
                onClick={onDismiss}
                className="text-amber-600 hover:text-amber-800"
              >
                <XCircle className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
};

/**
 * DedupeValidationModal Component
 * 
 * Pre-save modal that shows all cross-group duplicates.
 * BLOCK severity items prevent save until resolved.
 * WARN severity items can be acknowledged.
 * INFO severity items are informational only.
 */
export const DedupeValidationModal = ({
  isOpen,
  onClose,
  validationResult,
  onResolve,
  onProceed
}) => {
  if (!isOpen || !validationResult) return null;

  const { 
    has_duplicates, 
    duplicate_count, 
    blocking_count, 
    warning_count,
    info_count,
    can_proceed, 
    duplicates,
    summary 
  } = validationResult;

  // Group duplicates by severity
  const blockingDupes = duplicates.filter(d => d.severity === 'block');
  const warningDupes = duplicates.filter(d => d.severity === 'warn');
  const infoDupes = duplicates.filter(d => d.severity === 'info');

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'block':
        return <XCircle className="h-5 w-5 text-red-500" />;
      case 'warn':
        return <AlertTriangle className="h-5 w-5 text-amber-500" />;
      case 'info':
        return <Info className="h-5 w-5 text-blue-500" />;
      default:
        return null;
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'block':
        return 'bg-red-50 border-red-200';
      case 'warn':
        return 'bg-amber-50 border-amber-200';
      case 'info':
        return 'bg-blue-50 border-blue-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`px-6 py-4 border-b ${can_proceed ? 'bg-amber-50' : 'bg-red-50'}`}>
          <div className="flex items-center gap-3">
            {can_proceed ? (
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            ) : (
              <XCircle className="h-6 w-6 text-red-600" />
            )}
            <div>
              <h2 className={`text-lg font-semibold ${can_proceed ? 'text-amber-900' : 'text-red-900'}`}>
                {can_proceed ? 'Duplicate Operations Detected' : 'Cannot Save - Duplicates Must Be Resolved'}
              </h2>
              <p className={`text-sm ${can_proceed ? 'text-amber-700' : 'text-red-700'}`}>
                {summary}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Blocking duplicates */}
          {blockingDupes.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-red-800 mb-2 flex items-center gap-2">
                <XCircle className="h-4 w-4" />
                Must Resolve ({blockingDupes.length})
              </h3>
              <div className="space-y-2">
                {blockingDupes.map((dup, idx) => (
                  <DuplicateItem 
                    key={idx} 
                    duplicate={dup} 
                    onResolve={onResolve}
                    severityBg={getSeverityBg(dup.severity)}
                    severityIcon={getSeverityIcon(dup.severity)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Warning duplicates */}
          {warningDupes.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4" />
                Review Recommended ({warningDupes.length})
              </h3>
              <div className="space-y-2">
                {warningDupes.map((dup, idx) => (
                  <DuplicateItem 
                    key={idx} 
                    duplicate={dup} 
                    onResolve={onResolve}
                    severityBg={getSeverityBg(dup.severity)}
                    severityIcon={getSeverityIcon(dup.severity)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Info items */}
          {infoDupes.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-blue-800 mb-2 flex items-center gap-2">
                <Info className="h-4 w-4" />
                Information ({infoDupes.length})
              </h3>
              <div className="space-y-2">
                {infoDupes.map((dup, idx) => (
                  <DuplicateItem 
                    key={idx} 
                    duplicate={dup} 
                    onResolve={onResolve}
                    severityBg={getSeverityBg(dup.severity)}
                    severityIcon={getSeverityIcon(dup.severity)}
                    showActions={false}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          {can_proceed && (
            <button
              onClick={onProceed}
              className="px-4 py-2 text-white bg-amber-600 rounded-lg hover:bg-amber-700"
              data-testid="dedupe-proceed-btn"
            >
              Acknowledge & Continue
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Individual duplicate item display
 */
const DuplicateItem = ({ 
  duplicate, 
  onResolve, 
  severityBg, 
  severityIcon,
  showActions = true 
}) => {
  const [selectedResolution, setSelectedResolution] = React.useState('');

  const handleResolve = () => {
    if (selectedResolution && onResolve) {
      onResolve(duplicate.dedupe_key, selectedResolution, duplicate.locations);
    }
  };

  return (
    <div className={`p-3 rounded-lg border ${severityBg}`}>
      <div className="flex items-start gap-3">
        {severityIcon}
        <div className="flex-1">
          <div className="font-medium text-gray-900 text-sm">
            {duplicate.locations[0]?.line_description || duplicate.dedupe_key}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            <span className="font-medium">Dedupe Key:</span> {duplicate.dedupe_key}
            <span className="mx-2">•</span>
            <span className="font-medium">Scope:</span> {duplicate.scope}
          </div>
          <div className="text-xs text-gray-600 mt-1">
            <span className="font-medium">Found in:</span>
            {duplicate.locations.map((loc, idx) => (
              <span key={idx} className="ml-1">
                {idx > 0 && ', '}
                <span className="bg-gray-200 px-1.5 py-0.5 rounded">{loc.job_group_title}</span>
              </span>
            ))}
          </div>
          {duplicate.reason && (
            <div className="text-xs text-gray-500 mt-1 italic">{duplicate.reason}</div>
          )}

          {/* Resolution actions */}
          {showActions && duplicate.locations.length >= 2 && (() => {
            // iter211 — Smart-resolve hint. When one of the two JGs is
            // clearly a *combo / bundle* that already includes the
            // duplicated part (e.g. "Front Brake Discs & Pads" absorbs
            // a standalone "Front Brake Pads" line), recommend
            // removing from the standalone side. The heuristic is
            // intentionally conservative — we only flag a clear
            // bundle-vs-component pair to avoid surprising FOH.
            const _titles = duplicate.locations.map(l =>
              ((l.job_group_title || '') + '').toLowerCase()
            );
            const _bundleKeywords = [
              ['discs & pads', 'pads', 'discs+pads', 'brake discs & pads'],
              ['shocks & springs', 'shock', 'spring'],
              ['cambelt + water pump', 'cambelt', 'water pump'],
            ];
            let _smartIdx = -1; // index of the JG to KEEP
            for (const grp of _bundleKeywords) {
              const bundleTerm = grp[0];
              const componentTerms = grp.slice(1);
              const aIsBundle = _titles[0].includes(bundleTerm);
              const bIsBundle = _titles[1].includes(bundleTerm);
              const aIsComponent = componentTerms.some(t => _titles[0].includes(t));
              const bIsComponent = componentTerms.some(t => _titles[1].includes(t));
              if (aIsBundle && bIsComponent && !bIsBundle) { _smartIdx = 0; break; }
              if (bIsBundle && aIsComponent && !aIsBundle) { _smartIdx = 1; break; }
            }
            const _smartRemoveIdx = _smartIdx === -1 ? -1 : (_smartIdx === 0 ? 1 : 0);
            const _smartRemoveTitle = _smartRemoveIdx >= 0
              ? duplicate.locations[_smartRemoveIdx]?.job_group_title
              : null;
            const _smartKeepTitle = _smartIdx >= 0
              ? duplicate.locations[_smartIdx]?.job_group_title
              : null;
            return (
              <div className="mt-3 space-y-2">
                {_smartRemoveIdx !== -1 && (
                  <div
                    className="flex items-start gap-2 p-2 bg-emerald-50 border border-emerald-300 rounded text-xs"
                    data-testid={`dedupe-smart-hint-${duplicate.dedupe_key}`}
                  >
                    <span className="text-emerald-700 font-semibold shrink-0">Recommended:</span>
                    <span className="text-emerald-800">
                      "{_smartKeepTitle}" already includes this work — remove from "{_smartRemoveTitle}" and mark it as <em>Not Quoting · pads already included in disc combo</em>.
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <select
                    value={selectedResolution}
                    onChange={(e) => setSelectedResolution(e.target.value)}
                    className="text-xs border rounded px-2 py-1 bg-white"
                    data-testid={`dedupe-resolution-select-${duplicate.dedupe_key}`}
                  >
                    <option value="">Choose resolution...</option>
                    {_smartRemoveIdx !== -1 && (
                      <option value={_smartRemoveIdx === 0 ? "smart_remove_first" : "smart_remove_second"}>
                        ⭐ Smart: keep "{_smartKeepTitle}", drop &amp; mark "{_smartRemoveTitle}" as Not Quoting
                      </option>
                    )}
                    <option value="remove_from_first">
                      Remove from "{duplicate.locations[0]?.job_group_title}"
                    </option>
                    <option value="remove_from_second">
                      Remove from "{duplicate.locations[1]?.job_group_title}"
                    </option>
                    <option value="keep_both">Keep both (intentional)</option>
                  </select>
                  {selectedResolution && (
                    <button
                      onClick={handleResolve}
                      className="text-xs px-2 py-1 bg-gray-700 text-white rounded hover:bg-gray-800"
                      data-testid={`dedupe-apply-btn-${duplicate.dedupe_key}`}
                    >
                      Apply
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default DedupeValidationModal;
